import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

interface SimpleUser {
  username: string;
  displayUsername: string;
  chips: number;
}

interface AuthContextType {
  user: SimpleUser | null;
  loading: boolean;
  signInWithUsername: (username: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  updateChips: (newChips?: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Storage helper per garantir persistència al mòbil
const STORAGE_KEY = 'cartes_username';

const safeStorage = {
  get: (): string | null => {
    try {
      // Intentar localStorage primer
      const value = localStorage.getItem(STORAGE_KEY);
      if (value) return value;
      // Fallback a sessionStorage
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Storage read error:', e);
      return null;
    }
  },
  set: (value: string): void => {
    try {
      // Guardar a tots dos per garantir persistència
      localStorage.setItem(STORAGE_KEY, value);
      sessionStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      console.warn('Storage write error:', e);
      // Intentar només sessionStorage com a fallback
      try {
        sessionStorage.setItem(STORAGE_KEY, value);
      } catch (e2) {
        console.error('All storage failed:', e2);
      }
    }
  },
  remove: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Storage remove error:', e);
    }
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to extract display username
const getDisplayUsername = (fullUsername: string): string => {
  return fullUsername.length > 4 ? fullUsername.slice(0, -4) : fullUsername;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<SimpleUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        // Check if user is stored in storage (localStorage with fallback)
        const storedUsername = safeStorage.get();
        if (storedUsername) {
          // Fetch user data from database
          const { createClient } = await import('../lib/supabase');
          const supabase = createClient();

          const { data, error } = await supabase
            .from('profiles')
            .select('username, chips')
            .eq('username', storedUsername)
            .single();

          if (!error && data) {
            setUser({
              username: data.username,
              displayUsername: getDisplayUsername(data.username),
              chips: data.chips,
            });
          } else {
            // User doesn't exist in database, create them
            const isAdmin = storedUsername === 'admin47465';
            const startingChips = 1000;
            const { data: newUser, error: createError } = await supabase
              .from('profiles')
              .insert([
                {
                  username: storedUsername,
                  chips: startingChips,
                  role: isAdmin ? 'admin' : 'user',
                },
              ])
              .select('username, chips')
              .single();

            if (!createError && newUser) {
              setUser({
                username: newUser.username,
                displayUsername: getDisplayUsername(newUser.username),
                chips: newUser.chips,
              });
            } else {
              console.error('Error creating user on init:', createError);
              // Clear invalid stored username
              safeStorage.remove();
            }
          }
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        safeStorage.remove();
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  // Actualitzar fitxes periòdicament per sincronitzar amb la base de dades
  useEffect(() => {
    if (!user) return;

    const syncChips = async () => {
      const { createClient } = await import('../lib/supabase');
      const supabase = createClient();
      const { data } = await supabase
        .from('profiles')
        .select('chips')
        .eq('username', user.username)
        .single();

      if (data && typeof data.chips === 'number') {
        setUser((prev) => (prev ? { ...prev, chips: data.chips } : prev));
      }
    };

    syncChips(); // Sync immediatament
    const interval = setInterval(syncChips, 5000); // Sync cada 5 segons
    return () => clearInterval(interval);
  }, [user?.username]);

  const signInWithUsername = async (username: string) => {
    if (!username.trim()) {
      return { error: 'El nom d\'usuari és obligatori' };
    }

    try {
      const { createClient } = await import('../lib/supabase');
      const supabase = createClient();

      // Check if user exists
      const { data: existingUser, error: fetchError } = await supabase
        .from('profiles')
        .select('username, chips')
        .eq('username', username)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching user:', fetchError);
        return { error: `Error comprovant l'usuari: ${fetchError.message}` };
      }

      if (existingUser) {
        // User exists, use their data
        setUser({
          username: existingUser.username,
          displayUsername: getDisplayUsername(existingUser.username),
          chips: existingUser.chips,
        });
      } else {
        // Create new user
        const isAdmin = username === 'admin47465';
        const startingChips = 1000;
        const { data: newUser, error: createError } = await supabase
          .from('profiles')
          .insert([
            {
              username: username,
              chips: startingChips,
              role: isAdmin ? 'admin' : 'user',
            },
          ])
          .select('username, chips')
          .single();

        if (createError) {
          console.error('Error creating user:', createError);
          return { error: `Error creant l'usuari: ${createError.message}` };
        }

        if (newUser) {
          setUser({
            username: newUser.username,
            displayUsername: getDisplayUsername(newUser.username),
            chips: newUser.chips,
          });
        } else {
          return { error: 'L\'usuari no s\'ha creat correctament' };
        }
      }

      // Store username in storage (localStorage + sessionStorage fallback)
      safeStorage.set(username);
      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return {
        error: `S'ha produït un error inesperat: ${error instanceof Error ? error.message : 'Error desconegut'}`,
      };
    }
  };

  const signOut = async () => {
    safeStorage.remove();
    setUser(null);
  };

  const updateChips = async (newChips?: number) => {
    if (!user) return;

    if (typeof newChips === 'number') {
      // Assegurar que les fitxes mai siguin negatives
      const safeChips = Math.max(0, newChips);
      setUser((prev) => (prev ? { ...prev, chips: safeChips } : prev));
      
      // Update in database
      const { createClient } = await import('../lib/supabase');
      const supabase = createClient();
      await supabase
        .from('profiles')
        .update({ chips: safeChips })
        .eq('username', user.username);
    } else {
      // Fetch from DB
      const { createClient } = await import('../lib/supabase');
      const supabase = createClient();
      const { data } = await supabase
        .from('profiles')
        .select('chips')
        .eq('username', user.username)
        .single();

      if (data && typeof data.chips === 'number') {
        // Assegurar que les fitxes mai siguin negatives
        const safeChips = Math.max(0, data.chips);
        setUser((prev) => (prev ? { ...prev, chips: safeChips } : prev));
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signInWithUsername, signOut, updateChips }}>
      {children}
    </AuthContext.Provider>
  );
};
